import { useState } from 'preact/hooks';
import { Link, useLocation } from 'wouter-preact';
import {
  Home, LifeBuoy, Grid3x3, AlertTriangle, UsersRound, BarChart3, School, ClipboardList, PenLine, LogOut, ScanLine, IdCard,
  ListChecks, BookOpen, Scale, Settings, Award, TrendingUp, Table2, ClipboardCheck, MessageSquareText, FileText,
  ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight, Menu, X, GraduationCap, Building2, BookMarked, Dumbbell, User, ListTodo, Bot, MousePointerClick, Eye,
} from 'lucide-preact';
import { api, me } from '@/lib/teaching/teacher-api';
import { sidebarOpen, closeSidebar } from '@/lib/sidebar';

// Teacher OS sidebar (Design B). One <aside>: inline on laptop (expanded or
// icon-only), an off-canvas drawer on phone. Top level is life domains;
// only Teaching is built, the rest are greyed "Coming soon" rows, never links.

type Item = { path: string; label: string; icon: typeof Home };
type Group = { id: string; label: string; items: Item[] };

/** "My week" sits above the Teaching domain (slice 6 §07). Helpers goes
 *  here next: add { path: '/week/helpers', label: 'Helpers', icon: ... }
 *  after Commitments. */
export const MY_WEEK: Group = { id: 'myweek', label: 'My week', items: [
  { path: '/week/commitments', label: 'Commitments', icon: ListTodo },
  { path: '/week/agents', label: 'My agents', icon: Bot },
  { path: '/week/notes', label: 'Notes for Ali', icon: MousePointerClick },
] };

export const GROUPS: Group[] = [
  { id: 'today', label: 'Today', items: [
    { path: '/teaching', label: 'Home', icon: Home },
    { path: '/teaching/students', label: 'Who needs help', icon: LifeBuoy },
  ] },
  { id: 'mark', label: 'Mark', items: [
    { path: '/setup/scans', label: 'Scans and upload', icon: ScanLine },
    { path: '/setup/identity', label: 'Identity check', icon: IdCard },
    { path: '/setup/marks', label: 'Marks', icon: PenLine },
    { path: '/setup/review', label: 'Review queue', icon: ListChecks },
    { path: '/setup/moderation', label: 'Moderation', icon: Scale },
    { path: '/setup/judgements', label: 'MYP levels', icon: Award },
    { path: '/setup/feedback', label: 'Feedback', icon: MessageSquareText },
  ] },
  { id: 'understand', label: 'Understand', items: [
    { path: '/teaching/overview', label: 'Class overview', icon: BarChart3 },
    { path: '/teaching/analysis', label: 'Analysis', icon: Table2 },
    { path: '/teaching/heatmap', label: 'Mastery grid', icon: Grid3x3 },
    { path: '/teaching/misconceptions', label: 'Common mistakes', icon: AlertTriangle },
    { path: '/teaching/progress', label: 'Progress', icon: TrendingUp },
  ] },
  { id: 'act', label: 'Act', items: [
    { path: '/teaching/groups', label: 'Teaching groups', icon: UsersRound },
  ] },
  { id: 'finish', label: 'Finish', items: [
    { path: '/teaching/checkpoints', label: 'Checkpoints', icon: ClipboardCheck },
  ] },
  { id: 'setup', label: 'Set up', items: [
    { path: '/setup/classes', label: 'Classes and students', icon: School },
    { path: '/setup/tests', label: 'Tests', icon: ClipboardList },
    { path: '/setup/guide', label: 'Marking guide', icon: BookOpen },
    { path: '/setup/descriptors', label: 'MYP descriptors', icon: FileText },
  ] },
];

const SOON = [
  { label: 'ISRA', icon: Building2 },
  { label: 'Halaqa', icon: BookMarked },
  { label: 'Gym', icon: Dumbbell },
  { label: 'Personal', icon: User },
];

/** Set-up screens that work on one test and keep ?a= across the sidebar. */
const PER_TEST = ['/setup/marks', '/setup/review', '/setup/scans', '/setup/identity', '/setup/guide', '/setup/moderation', '/setup/judgements', '/setup/descriptors', '/setup/feedback'];

/** Carry only the assessment id across screens; class filters differ per page. */
function carry(path: string): string {
  const a = new URLSearchParams(window.location.search).get('a');
  const cls = new URLSearchParams(window.location.search).get('class');
  if (path === '/teaching/progress') return path;
  if (path.startsWith('/teaching')) return path + (a || cls ? '?' + new URLSearchParams({ ...(a ? { a } : {}), ...(cls ? { class: cls } : {}) }) : '');
  if (PER_TEST.includes(path) && a) return `${path}?a=${a}`;
  return path;
}

async function signOut() {
  await api('POST', '/api/auth/logout').catch(() => undefined);
  me.value = null;
  location.assign('/login');
}

function isActive(pathname: string, path: string): boolean {
  if (path === '/teaching') return pathname === '/teaching' || pathname === '/' || pathname.startsWith('/teaching/a/');
  if (path === '/teaching/progress') return pathname.startsWith(path) || /^\/teaching\/student\/[^/]+\/progress/.test(pathname);
  return pathname.startsWith(path);
}

// ── Remembered UI state (per browser; storage may be unavailable) ──────

const K_COLLAPSED = 'tos.sidebar.collapsed';
const K_GROUPS = 'tos.sidebar.groups';
const DEFAULT_OPEN: Record<string, boolean> = { myweek: true, today: true, mark: true, understand: true, act: false, finish: false, setup: false };

function read<T>(key: string, fallback: T): T {
  try { const v = localStorage.getItem(key); return v === null ? fallback : JSON.parse(v) as T; } catch { return fallback; }
}
function write(key: string, v: unknown) {
  try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* private window: keep in memory */ }
}

const rowBase = 'flex items-center gap-2.5 rounded-lg text-[13.5px] transition-colors';

function NavItem({ item, active, collapsed }: { item: Item; active: boolean; collapsed: boolean }) {
  const Icon = item.icon;
  return (
    <Link
      href={carry(item.path)}
      onClick={closeSidebar}
      title={collapsed ? item.label : undefined}
      aria-label={collapsed ? item.label : undefined}
      aria-current={active ? 'page' : undefined}
      data-nav={item.path}
      class={[
        rowBase,
        collapsed ? 'w-9 h-8 justify-center mx-auto' : 'h-8 px-2.5',
        active ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent)] font-semibold' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-elevated)]',
      ].join(' ')}
    >
      <Icon size={16} class="shrink-0" />
      {!collapsed && <span class="truncate">{item.label}</span>}
    </Link>
  );
}

export function Rail() {
  const [pathname] = useLocation();
  const [collapsed, setCollapsedState] = useState<boolean>(() => read(K_COLLAPSED, false));
  const [open, setOpenState] = useState<Record<string, boolean>>(() => ({ ...DEFAULT_OPEN, ...read<Record<string, boolean>>(K_GROUPS, {}) }));
  const drawer = sidebarOpen.value;
  // The phone drawer always shows labels.
  const iconsOnly = collapsed && !drawer;

  const setCollapsed = (v: boolean) => { setCollapsedState(v); write(K_COLLAPSED, v); };
  const toggleGroup = (id: string) => { const next = { ...open, [id]: !open[id] }; setOpenState(next); write(K_GROUPS, next); };

  const renderGroup = (g: Group) => {
    const hasActive = g.items.some((it) => isActive(pathname, it.path));
    const isOpen = iconsOnly || open[g.id] || hasActive;
    return (
      <div key={g.id} data-group={g.id} class={iconsOnly ? 'mt-1 pt-1 border-t border-[var(--color-border)] first:border-0' : g.id === 'myweek' ? '' : 'mt-2'}>
        {!iconsOnly && (
          <button type="button" onClick={() => toggleGroup(g.id)} aria-expanded={isOpen} data-group-toggle={g.id}
            class="w-full flex items-center justify-between h-7 px-2.5 text-[var(--color-text-faint)] hover:text-[var(--color-text-muted)]">
            {/* Template's unlayered `button { font: inherit }` beats Tailwind text sizes; style the span. */}
            <span class="text-[10.5px] font-semibold uppercase tracking-[0.08em]">{g.label}</span>
            {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        )}
        {isOpen && <div class="flex flex-col gap-0.5">{g.items.map((it) => <NavItem key={it.path} item={it} active={isActive(pathname, it.path)} collapsed={iconsOnly} />)}</div>}
      </div>
    );
  };

  return (
    <>
      <button type="button" onClick={() => { sidebarOpen.value = true; }} aria-label="Open menu"
        class="md:hidden fixed top-3 left-3 z-40 w-9 h-9 grid place-items-center rounded-lg bg-[var(--color-card)] border border-[var(--color-border)] text-[var(--color-text)] shadow-sm">
        <Menu size={18} />
      </button>
      {drawer && <div class="md:hidden fixed inset-0 bg-black/40 z-40" onClick={closeSidebar} />}

      <aside
        data-sidebar={iconsOnly ? 'collapsed' : 'expanded'}
        class={[
          'flex flex-col shrink-0 bg-[var(--color-sidebar)] border-r border-[var(--color-border)] h-full',
          'fixed inset-y-0 left-0 z-50 w-[260px] transition-transform md:static md:z-auto md:translate-x-0',
          drawer ? 'translate-x-0 shadow-xl' : '-translate-x-full',
          iconsOnly ? 'md:w-16' : 'md:w-[232px]',
        ].join(' ')}
      >
        <div class={'flex items-center shrink-0 h-14 ' + (iconsOnly ? 'justify-center' : 'px-4 justify-between')}>
          {!iconsOnly && <Link href="/teaching" onClick={closeSidebar} class="text-[15px] font-bold tracking-[-0.01em] text-[var(--color-text)]">Teacher OS</Link>}
          <button type="button" onClick={() => setCollapsed(!collapsed)} data-sidebar-toggle
            aria-label={iconsOnly ? 'Expand sidebar' : 'Collapse sidebar'} aria-expanded={!iconsOnly} title={iconsOnly ? 'Expand sidebar' : 'Collapse sidebar'}
            class={'hidden md:inline-flex items-center gap-1 rounded-md text-[12px] text-[var(--color-text-faint)] hover:text-[var(--color-text)] hover:bg-[var(--color-elevated)] ' + (iconsOnly ? 'w-9 h-8 justify-center' : 'px-1.5 h-7')}>
            {iconsOnly ? <ChevronsRight size={16} /> : <><ChevronsLeft size={14} /><span class="text-[12px]">collapse</span></>}
          </button>
          <button type="button" onClick={closeSidebar} aria-label="Close menu" class="md:hidden w-8 h-8 grid place-items-center rounded-md text-[var(--color-text-muted)]"><X size={18} /></button>
        </div>

        <nav aria-label="Teacher OS" class={'flex-1 min-h-0 overflow-y-auto pb-3 ' + (iconsOnly ? 'px-2' : 'px-3')}>
          <div class={iconsOnly ? 'mb-1 pb-1 border-b border-[var(--color-border)]' : 'mb-3 pb-3 border-b border-[var(--color-border)]'} data-domain="My week">
            {renderGroup(me.value?.admin ? { ...MY_WEEK, items: [...MY_WEEK.items, { path: '/admin', label: 'All teachers', icon: Eye }] } : MY_WEEK)}
          </div>
          <div class={'flex items-center gap-2 h-8 rounded-lg text-[var(--color-text)] font-semibold text-[13.5px] ' + (iconsOnly ? 'justify-center' : 'px-2.5')} data-domain="Teaching" title={iconsOnly ? 'Teaching' : undefined}>
            <GraduationCap size={16} class="shrink-0 text-[var(--color-accent)]" />{!iconsOnly && 'Teaching'}
          </div>
          {GROUPS.map(renderGroup)}
          <div class={'mt-3 pt-2 border-t border-[var(--color-border)] flex flex-col gap-0.5'}>
            {SOON.map((d) => {
              const Icon = d.icon;
              return (
                <div key={d.label} data-domain={d.label} data-coming-soon aria-disabled="true" title={`${d.label}: coming soon`}
                  class={'flex items-center gap-2.5 h-8 rounded-lg text-[13.5px] text-[var(--color-text-faint)] opacity-60 cursor-default select-none ' + (iconsOnly ? 'justify-center' : 'px-2.5')}>
                  <Icon size={16} class="shrink-0" />
                  {!iconsOnly && <><span>{d.label}</span><span class="ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[var(--color-elevated)] text-[var(--color-text-muted)]">Coming soon</span></>}
                </div>
              );
            })}
          </div>
        </nav>

        <div class={'shrink-0 border-t border-[var(--color-border)] flex items-center gap-1 ' + (iconsOnly ? 'flex-col py-2' : 'px-3 py-2.5')}>
          <Link href="/setup/settings" onClick={closeSidebar} title={iconsOnly ? 'Settings' : undefined} aria-label={iconsOnly ? 'Settings' : undefined}
            aria-current={pathname.startsWith('/setup/settings') ? 'page' : undefined}
            class={[rowBase, iconsOnly ? 'w-9 h-8 justify-center' : 'h-8 px-2.5 flex-1', pathname.startsWith('/setup/settings') ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent)] font-semibold' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-elevated)]'].join(' ')}>
            <Settings size={16} class="shrink-0" />{!iconsOnly && 'Settings'}
          </Link>
          <button type="button" onClick={signOut} aria-label="Log out" title="Log out"
            class={rowBase + ' text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-elevated)] ' + (iconsOnly ? 'w-9 h-8 justify-center' : 'h-8 px-2.5')}>
            <LogOut size={16} class="shrink-0" />{!iconsOnly && <span class="text-[13.5px]">Log out</span>}
          </button>
        </div>
      </aside>
    </>
  );
}
